import React, { useEffect, useState } from "react";

/**
 * SessionUrgencyTimer
 * --------------------------------------------------------------------------
 * Compact MM:SS countdown that:
 *   - Defaults to 5 minutes.
 *   - Persists the target timestamp in sessionStorage so it survives client-
 *     side navigation but resets on a fresh tab/session.
 *   - When the timer hits 0, it auto-renews for another `durationMs` (so the
 *     urgency cue never feels broken to visitors who linger).
 *
 * Props:
 *   storageKey   stable id per surface (e.g. "ebook-popup", "ebook-book").
 *   durationMs   total ms when timer first starts (default 5 minutes).
 *   label        small label above the digits (default "Offer expires in").
 *   variant      "compact" (default — used in popup / inline)
 *                | "card"  (larger box style — used on the book section).
 */

const PREFIX = "btk_urgency_v1:";

function readTarget(storageKey, durationMs) {
  try {
    const raw = sessionStorage.getItem(PREFIX + storageKey);
    if (raw) {
      const t = parseInt(raw, 10);
      if (Number.isFinite(t) && t > Date.now()) return t;
    }
  } catch (_) {
    /* sessionStorage unavailable — fall through */
  }
  const fresh = Date.now() + durationMs;
  try {
    sessionStorage.setItem(PREFIX + storageKey, String(fresh));
  } catch (_) { /* noop */ }
  return fresh;
}

function clearTarget(storageKey) {
  try {
    sessionStorage.removeItem(PREFIX + storageKey);
  } catch (_) { /* noop */ }
}

function remaining(target) {
  const ms = Math.max(0, target - Date.now());
  const totalSec = Math.floor(ms / 1000);
  return {
    minutes: Math.floor(totalSec / 60),
    seconds: totalSec % 60,
    done: ms === 0,
  };
}

export default function SessionUrgencyTimer({
  storageKey,
  durationMs = 5 * 60 * 1000,
  label = "Offer expires in",
  variant = "compact",
}) {
  const [target, setTarget] = useState(() => readTarget(storageKey, durationMs));
  const [now, setNow] = useState(() => remaining(target));

  useEffect(() => {
    const id = setInterval(() => {
      const r = remaining(target);
      setNow(r);
      if (r.done) {
        // Auto-renew so the urgency cue never feels stale during the visit.
        clearTarget(storageKey);
        setTarget(readTarget(storageKey, durationMs));
      }
    }, 1000);
    return () => clearInterval(id);
  }, [target, storageKey, durationMs]);

  const mm = String(now.minutes).padStart(2, "0");
  const ss = String(now.seconds).padStart(2, "0");
  const isCard = variant === "card";

  return (
    <div
      data-testid={`urgency-timer-${storageKey}`}
      className={
        isCard
          ? "inline-flex flex-col items-center gap-2"
          : "inline-flex flex-col items-center gap-1.5"
      }
    >
      <span
        className={
          isCard
            ? "text-[10px] md:text-[11px] uppercase tracking-[0.28em] text-gold/85"
            : "text-[10px] uppercase tracking-[0.28em] text-gold/85"
        }
      >
        {label}
      </span>
      <div
        className={
          isCard
            ? "flex items-center gap-2 md:gap-3"
            : "flex items-center gap-1.5"
        }
      >
        <span
          className={
            isCard
              ? "bk-card rounded-xl px-4 md:px-5 py-2 md:py-3 font-display text-3xl md:text-4xl tabular-nums"
              : "bk-card rounded-lg px-2.5 py-1 font-display text-xl tabular-nums"
          }
        >
          <span className="bk-gold-text">{mm}</span>
        </span>
        <span className={isCard ? "text-gold/70 text-2xl" : "text-gold/70"}>:</span>
        <span
          className={
            isCard
              ? "bk-card rounded-xl px-4 md:px-5 py-2 md:py-3 font-display text-3xl md:text-4xl tabular-nums"
              : "bk-card rounded-lg px-2.5 py-1 font-display text-xl tabular-nums"
          }
        >
          <span className="bk-gold-text">{ss}</span>
        </span>
      </div>
      {isCard ? (
        <div className="flex items-center gap-12 md:gap-14 text-[10px] uppercase tracking-[0.28em] text-white/55 mt-1">
          <span>Minutes</span>
          <span>Seconds</span>
        </div>
      ) : null}
    </div>
  );
}
